<template>
  <DataTable
    :filters="filters"
    :expandedRows="expandedRows"
    :value="vacations"
    :totalRecords="resolvedTotalRecords"
    :paginator="true"
    dataKey="id"
    tableStyle="min-width: 60rem"
    class="tabla mb-3"
    :rows="5"
    :rowsPerPageOptions="[5, 10, 20, 50]"
    filter-display="row"
    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
    :currentPageReportTemplate="reportTemplate"
    :globalFilterFields="globalFilterFields"
    @update:filters="onUpdateFilters"
    @update:expandedRows="onUpdateExpandedRows"
    @rowExpand="emit('row-expand', $event)"
  >
    <template #header>
      <div class="flex justify-content-end">
        <span class="p-input-icon-left">
          <i class="pi pi-search" />
          <InputText v-model="globalFilterValue" placeholder="Buscar" />
        </span>
      </div>
    </template>
    <Column expander style="width: 5rem" />
    <Column
      field="globalStatus"
      header="Estado"
      :showFilterMenu="false"
      :filterMenuStyle="{ width: '3rem' }"
    >
      <template #body="{ data, field }">
        <Tag
          :value="formatRequestState(data[field])"
          :severity="getStatusLabel(data[field])"
        ></Tag>
      </template>
      <template #filter="{ filterModel, filterCallback }">
        <Dropdown
          v-model="filterModel.value"
          @change="filterCallback()"
          :options="statuses"
          placeholder="filtrar estado"
          class="p-column-filter flex align-items-center"
          style="min-width: 12rem; height: 3rem"
          :showClear="true"
        >
          <template #value="{ value }">
            <span class="text-sm text-600 font-medium">
              {{ value && formatRequestState(value) }}
            </span>
          </template>
          <template #option="slotProps">
            <Tag
              :value="formatRequestState(slotProps.option)"
              :severity="getStatusLabel(slotProps.option)"
            />
          </template>
        </Dropdown>
      </template>
    </Column>
    <Column header="Descripción" field="observation">
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column header="Cantidad" field="quantity" sortable>
      <template #body="{ data, field }">
        {{ data[field] }}
      </template>
    </Column>
    <Column
      header="Fecha de inicio"
      filter-field="dateInit"
      :show-filter-menu="false"
      field="dateInit"
      sortable
    ></Column>
    <Column header="Fecha final" field="dateEnd" sortable></Column>
    <Column header="Aprobadores">
      <template #body="{ data }">
        {{ data.approvedCount }} / {{ data.approversCount }}
      </template>
    </Column>
    <Column field="createdAt" header="Fecha de creación"> </Column>
    <Column header="Acciones">
      <template #body="{ data }">
        <div class="actions">
          <ConfirmPopup></ConfirmPopup>
          <Button
            v-if="canShowReceiptButton(data.globalStatus, companyCode)"
            v-tooltip.bottom="'Ver recibo'"
            class="action-btn view hover:text-blue-800 mr-2"
            severity="primary"
            icon="pi pi-print"
            @click="emit('view-receipt', data.id)"
          ></Button>
          <Button
            v-if="data.file"
            v-tooltip.bottom="'ver adjunto'"
            class="action-btn view hover:text-blue-800 mr-2"
            severity="secondary"
            icon="pi pi-paperclip"
            @click="emit('download-attachment', data.file)"
          ></Button>
          <Button
            v-if="canShowDeleteButton(data)"
            v-tooltip.bottom="'Eliminar solicitud'"
            class="action-btn view btn hover:text-blue-800"
            severity="danger"
            icon="pi pi-trash"
            @click="
              emit('delete-request', {
                originalEvent: $event,
                requestId: data.id,
              })
            "
          ></Button>
        </div>
      </template>
    </Column>
    <template #expansion="slotProps">
      <div class="p-3">
        <h5>Detalle de aprobadores</h5>
        <DataTable :value="slotProps.data.steps">
          <template
            #empty
            v-if="slotProps.data.origin === 'PAYROLLSYSTEM'"
            class="flex flex-justify-center"
          >
            Solicitud creada desde el sistema de nómina
          </template>
          <template
            #empty
            v-else-if="slotProps.data.origin === 'RRHH'"
            class="flex flex-justify-center"
          >
            Solicitud creada por el área de RRHH
          </template>
          <template #empty v-else class="flex flex-justify-center">
            <i class="pi pi-spin pi-spinner" style="font-size: 1rem"></i>
            cargando...
          </template>
          <Column field="approver.name" header="Nombre"></Column>
          <Column field="resolutionDate" header="Fecha de resolución">
            <template #body="{ data, field }">
              <span v-if="data[field]" v-local-date="data[field]"></span>
              <span class="text-600 font-medium" v-else>Sin definir</span>
            </template>
          </Column>
          <Column field="weight" header="Prioridad"></Column>
          <Column field="resolutionStatus" header="Estado">
            <template #body="slotProps">
              <Tag
                :value="formatRequestState(slotProps.data.resolutionStatus)"
                :severity="getStatusLabel(slotProps.data.resolutionStatus)"
              >
              </Tag>
            </template>
          </Column>
          <Column field="resolutionComment" header="Comentario"> </Column>
        </DataTable>
      </div>
    </template>
  </DataTable>
</template>

<script setup lang="ts">
  import { computed, toRefs } from 'vue'
  import DataTable, {
    type DataTableExpandedRows,
    type DataTableFilterMeta,
    type DataTableFilterMetaData,
  } from 'primevue/datatable'
  import Column from 'primevue/column'
  import Dropdown from 'primevue/dropdown'
  import Tag from 'primevue/tag'
  import InputText from 'primevue/inputtext'
  import Button from 'primevue/button'
  import ConfirmPopup from 'primevue/confirmpopup'
  import type Vacation from '@/modules/employees/models/Vacation'

  const props = defineProps<{
    vacations?: Vacation[]
    filters: DataTableFilterMeta
    expandedRows: DataTableExpandedRows | any[]
    statuses: string[]
    companyCode?: string
    totalRecords?: number
  }>()

  const emit = defineEmits<{
    (event: 'update:filters', value: DataTableFilterMeta): void
    (event: 'update:expandedRows', value: DataTableExpandedRows | any[]): void
    (event: 'row-expand', value: unknown): void
    (event: 'view-receipt', value: string): void
    (event: 'download-attachment', value: string): void
    (
      event: 'delete-request',
      value: { originalEvent: MouseEvent; requestId: string }
    ): void
  }>()

  const onUpdateFilters = (value: DataTableFilterMeta) => {
    emit('update:filters', value)
  }

  const onUpdateExpandedRows = (value: DataTableExpandedRows | any[]) => {
    emit('update:expandedRows', value)
  }

  const {
    filters,
    expandedRows,
    vacations,
    statuses,
    companyCode,
    totalRecords,
  } = toRefs(props)

  const globalFilterFields = [
    'observation',
    'quantity',
    'dateInit',
    'dateEnd',
    'createdAt',
  ]

  const globalFilterValue = computed<string | null>({
    get: () => {
      const globalMeta = filters.value.global as
        | DataTableFilterMetaData
        | undefined
      return (globalMeta?.value as string | null | undefined) ?? null
    },
    set: (value) => {
      const currentFilters: DataTableFilterMeta = { ...filters.value }
      const globalMeta = currentFilters.global as
        | DataTableFilterMetaData
        | undefined
      currentFilters.global = {
        matchMode: globalMeta?.matchMode ?? 'contains',
        value,
      }
      emit('update:filters', currentFilters)
    },
  })

  const formatRequestState = (state: string) => {
    switch (state) {
      case 'PENDING':
        return 'Pendiente'
      case 'REJECTED':
        return 'Rechazado'
      case 'APPROVED':
        return 'Aprobado'
      default:
        return 'Pendiente'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success'
      case 'PENDING':
        return 'warning'
      case 'REJECTED':
        return 'danger'
      default:
        return 'warning'
    }
  }

  const canShowReceiptButton = (status: string, code?: string) => {
    return status === 'APPROVED' && !['27', '28'].includes(code || '')
  }

  const canShowDeleteButton = (vacation: Vacation) => {
    return (
      vacation.approvedCount === 0 &&
      vacation.globalStatus === 'PENDING' &&
      vacation.origin !== 'PAYROLLSYSTEM'
    )
  }

  const resolvedTotalRecords = computed(
    () => totalRecords.value ?? vacations.value?.length ?? 0
  )

  const reportTemplate = computed(
    () => '{first} a {last} de ' + resolvedTotalRecords.value
  )
</script>

<style scoped>
  .tabla {
    margin-top: 1rem;
    box-shadow: 0px 1px 7px rgba(0, 0, 0, 0.1),
      0px 4px 5px -2px rgba(0, 0, 0, 0.12),
      0px 10px 15px -5px rgba(0, 0, 0, 0.2);
    border-radius: 16px;
    width: 90%;
  }

  .actions {
    display: flex;
    flex-direction: row;
    width: auto;
  }

  .action-btn {
    height: 20px;
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :deep(.p-column-title) {
    font-size: 14px;
  }

  :deep(.p-datatable-tbody) {
    font-size: 14px;
  }
</style>
